import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ProductType } from "@/core";
import { useProductsStore } from "@/stores/products";
import { BarChart3, DollarSign, Package, TrendingUp } from "lucide-react";

export default function DashboardPage() {
  const { products } = useProductsStore();
  const totalProducts = products.length;
  const activeProducts = products.filter((p) => !p.inactive).length;
  const avgSellPrice =
    products
      .filter((p) => p.sellPrices.length > 0)
      .reduce((acc, p) => {
        const avgPrice =
          p.sellPrices.reduce((sum, price) => sum + price.price, 0) /
          p.sellPrices.length;
        return acc + avgPrice;
      }, 0) / products.filter((p) => p.sellPrices.length > 0).length;
  const avgBuyPrice =
    products
      .filter((p) => p.buyPrices?.length > 0)
      .reduce((acc, p) => {
        const avgPrice =
          p.buyPrices.reduce((sum, price) => sum + price.price, 0) /
          p.buyPrices?.length;
        return acc + avgPrice;
      }, 0) / products.filter((p) => p.buyPrices?.length > 0).length;

  return (
    <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8">
      {/* Product Statistics Section */}
      <div>
        <h2 className="text-2xl font-bold mb-6">Product Statistics</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <Card className="bg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Products
              </CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalProducts}</div>
              <p className="text-xs text-muted-foreground">
                All products in catalog
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Active Products
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeProducts}</div>
              <p className="text-xs text-muted-foreground">
                Currently available
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Avg Sell Price
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${avgSellPrice.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">Per mile/point</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Avg Buy Price
              </CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${avgBuyPrice.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">Per mile/point</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Charts Section */}
      {/* <div className="mt-8 grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Average Price Comparison</CardTitle>
            <CardDescription>
              Buy vs Sell prices across products
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[25vh] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={products.map((product) => ({
                    name: product.name.split(" ")[0], // Short name
                    sellPrice:
                      product.sellPrices.length > 0
                        ? (product.sellPrices.reduce(
                            (sum, p) => sum + p.price,
                            0,
                          ) /
                            product.sellPrices.length) *
                          1000
                        : 0,
                    buyPrice:
                      product.buyPrices?.length > 0
                        ? (product.buyPrices.reduce(
                            (sum, p) => sum + p.price,
                            0,
                          ) /
                            product.buyPrices?.length) *
                          1000
                        : 0,
                  }))}
                  margin={{ top: 20, right: 30, left: 30, bottom: 50 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 14 }}
                    tickLine={{ stroke: "#e5e7eb" }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    label={{
                      value: "Price ($)",
                      angle: -90,
                      position: "insideLeft",
                      style: { fontSize: 14 },
                    }}
                    tick={{ fontSize: 14 }}
                    tickLine={{ stroke: "#e5e7eb" }}
                  />
                  <Tooltip
                    formatter={(value: number) => [`$${value / 1000}`, ""]}
                    labelFormatter={(label) => `${label}`}
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                  <Bar
                    dataKey="sellPrice"
                    fill="#3b82f6"
                    name="Sell Price"
                    radius={[2, 2, 0, 0]}
                  />
                  <Bar
                    dataKey="buyPrice"
                    fill="#8b5cf6"
                    name="Buy Price"
                    radius={[2, 2, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Product Type Distribution</CardTitle>
            <CardDescription>
              Distribution of products by buy/sell type
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0">
            <div className="h-[25vh] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      {
                        name: "Both",
                        value: products.filter(
                          (p: Product) => p.buyOrSell === ProductType.BOTH,
                        ).length,
                        fill: "#64748b", // Neutral slate color
                      },
                      {
                        name: "Sell Only",
                        value: products.filter(
                          (p: Product) => p.buyOrSell === ProductType.SELL,
                        ).length,
                        fill: "#94a3b8", // Lighter slate
                      },
                      {
                        name: "Buy Only",
                        value: products.filter(
                          (p: Product) => p.buyOrSell === ProductType.BUY,
                        ).length,
                        fill: "#475569", // Darker slate
                      },
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      percent && percent > 0
                        ? `${name} ${(percent * 100).toFixed(0)}%`
                        : ""
                    }
                    outerRadius={100}
                    style={{
                      fontSize: 14,
                    }}
                    innerRadius={40}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="rgba(0,0,0,0.1)"
                    strokeWidth={1}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div> */}

      {/* Product Details Table */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Product Overview</CardTitle>
          <CardDescription>
            Detailed view of all products and their pricing
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="relative group whitespace-nowrap text-left border-r last:border-r-0 h-10 px-2 align-middle">
                    <div className="flex items-center justify-start">
                      Product Name
                    </div>
                  </th>
                  <th className="relative group whitespace-nowrap text-left border-r last:border-r-0 h-10 px-2 align-middle">
                    <div className="flex items-center justify-start">
                      Status
                    </div>
                  </th>
                  <th className="relative group whitespace-nowrap text-left border-r last:border-r-0 h-10 px-2 align-middle">
                    <div className="flex items-center justify-start">Type</div>
                  </th>
                  <th className="relative group whitespace-nowrap text-right border-r last:border-r-0 h-10 px-2 align-middle">
                    <div className="flex items-center justify-end">
                      Sell Range
                    </div>
                  </th>
                  <th className="relative group whitespace-nowrap text-right border-r last:border-r-0 h-10 px-2 align-middle">
                    <div className="flex items-center justify-end">
                      Buy Range
                    </div>
                  </th>
                  <th className="relative group whitespace-nowrap text-left border-r last:border-r-0 h-10 px-2 align-middle">
                    <div className="flex items-center justify-start">
                      Last Updated
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} className="hover:bg-muted/50 border-b">
                    <td className="whitespace-nowrap text-left border-r last:border-r-0 p-2 font-medium">
                      {product.name}
                    </td>
                    <td className="whitespace-nowrap text-left border-r last:border-r-0 p-2">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-xl text-xs font-medium ${
                          product.inactive
                            ? "bg-muted text-muted-foreground"
                            : "bg-primary/10 text-primary hover:bg-primary/10"
                        }`}
                      >
                        {product.inactive ? "Inactive" : "Active"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap text-left border-r last:border-r-0 p-2">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-xl text-xs font-medium ${
                          product.buyOrSell === ProductType.BOTH
                            ? "bg-primary/10 text-primary hover:bg-primary/10"
                            : "bg-secondary text-secondary-foreground hover:bg-muted"
                        }`}
                      >
                        {product.buyOrSell}
                      </span>
                    </td>
                    <td className="whitespace-nowrap text-right border-r last:border-r-0 p-2">
                      {product.sellPrices.length > 0
                        ? `${product.minimumValueForSell.toLocaleString()} - ${product.maximumValueForSell.toLocaleString()}`
                        : "N/A"}
                    </td>
                    <td className="whitespace-nowrap text-right border-r last:border-r-0 p-2">
                      {product.buyPrices?.length > 0
                        ? `${product.minimumValueForBuy.toLocaleString()} - ${product.maximumValueForBuy.toLocaleString()}`
                        : "N/A"}
                    </td>
                    <td className="whitespace-nowrap text-left border-r last:border-r-0 p-2 text-muted-foreground">
                      {product.updatedAt
                        ? product.updatedAt.toLocaleDateString()
                        : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
